import React from 'react';
import { Modal } from '@brighterion/br-ui';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { RQ_TENANT_GROUP_OVERRIDES, RQ_TENANT_GROUP_OVERRIDES_INHERITED } from 'Utils';
import useDeleteTenantGroupOverride from 'Hooks/query-hooks/tenant/useDeleteTenantGroupOverride';
import useAlertBanner from 'Hooks/reduxHelperHooks/useAlertBanner';
import useGetTenantGroup from 'Hooks/query-hooks/tenant-groups/useGetTenantGroup';
import { EventNames, EventTypes, trackEvent } from '@brighterion/insights';
// eslint-disable-next-line max-len
import useDeleteTenantGroupOverrideErrorHandler from 'Hooks/query-hooks/tenant/useDeleteTenantGroupOverrideErrorHandler';
import { RulesTypes } from 'Common/enums';

import styles from './DeleteTenantGroupOverrideModal.css';

type Props = {
    isOpen: boolean;
    closeModal: () => void;
    id: string;
    showButton?: boolean;
    ruleType: RulesTypes;
};

// Only landlord user can see this component
const DeleteTenantGroupOverrideModal = ({ closeModal, isOpen, id, showButton, ruleType }: Props) => {
    const { tenantGroupId } = useParams<{ tenantGroupId: string }>();
    const { t } = useTranslation();
    const { data: tenantGroupData } = useGetTenantGroup({
        tenantGroupId,
        queryEnabled: showButton
    });

    const { addAlert } = useAlertBanner();
    const queryClient = useQueryClient();
    const errorHandler = useDeleteTenantGroupOverrideErrorHandler();
    const onSuccess = () => {
        addAlert({
            id: 'remove-rule',
            content: t('Tenant Group Override removed', { id }),
            status: 'success'
        });
        closeModal();
        queryClient.invalidateQueries([RQ_TENANT_GROUP_OVERRIDES]);
        queryClient.invalidateQueries([RQ_TENANT_GROUP_OVERRIDES_INHERITED]);
    };

    const deleteMutation = useDeleteTenantGroupOverride({
        tenantGroupId,
        tenantGroupName: tenantGroupData?.name,
        ruleId: id,
        mutationOptions: { onError: errorHandler, onSuccess }
    });

    const handleTrackEventClick = () => {
        let clickIdentifier = '';
        let text = '';
        if (ruleType === RulesTypes.ADVANCED) {
            clickIdentifier = 'Advanced Tenant Group Rule - Remove Override';
            text = 'Remove Advanced Tenant Group Rule Override';
        } else if (ruleType === RulesTypes.SIMPLE) {
            clickIdentifier = 'Tenant Groups - Remove Override';
            text = 'Remove Override';
        }

        if (!clickIdentifier || !text) {
            return;
        }

        trackEvent({
            eventType: EventTypes.track,
            eventName: EventNames.click,
            payload: {
                clickIdentifier,
                text
            }
        });
    };

    return (
        <Modal
            isOpen={isOpen}
            closeModal={closeModal}
            containerClass={styles.modalContainer}
            status='warning'
            dataTestId='delete-tenant-group-override-modal'
        >
            <Modal.Title
                id='remove-tenant-group-override-modal-title'
                title={t('Remove this Tenant Group Override?')}
                onClose={closeModal}
                showCloseIcon={false}
                statusIcon='warning'
            />
            <div className={styles.deleteWarningContent}>
                <div className={styles.tenantGroupInfo}>
                    {t('Remove Tenant Group override warning', {
                        id
                    })}
                </div>
                {t('This cannot be undone')}.
            </div>
            <Modal.Footer
                id='remove-tenant-group-override-modal-footer'
                onActionText='Remove Override'
                disableOnAction={deleteMutation?.isLoading}
                disableOnCancel={deleteMutation?.isLoading}
                onClose={closeModal}
                onAction={() => {
                    deleteMutation.mutate([id]);
                    handleTrackEventClick();
                }}
            />
        </Modal>
    );
};

export default DeleteTenantGroupOverrideModal;
